import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebaseConfig";
import ro from "/src/content/ro.json";
import {
  collection,
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import ReCAPTCHA from "react-google-recaptcha";

const Recenzii = () => {
  const [recenzii, setRecenzii] = useState([]);
  const [nouaRecenzie, setNouaRecenzie] = useState({
    nume: "",
    email: "",
    text: "",
    rating: 5,
  });
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const recaptchaRef = useRef(null);

  const recenziiRef = collection(db, "recenzii");

  // Monitorizam autentificarea utilizatorului
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Citim recenziile existente + completam imaginea
  useEffect(() => {
    const fetchRecenzii = async () => {
      try {
        const q = query(recenziiRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setRecenzii([]);
          return;
        }

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          photoURL: doc.data().photoURL || "/images/imgaine1.svg",
        }));

        setRecenzii(data);
      } catch (err) {
        console.error("❌ Eroare la citirea recenziilor:", err);
        setFormError(ro.recenzii.errors.load);
      }
    };

    fetchRecenzii();
  }, []);

  // Actualizam campurile formularului
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNouaRecenzie((p) => ({ ...p, [name]: value }));
  };

  const normalizeEmail = (value) => (value || "").trim().toLowerCase();

  // Trimitem recenzia
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setLoading(true);

    try {
      const { nume, email, text, rating } = nouaRecenzie;
      if (!nume.trim() || !text.trim() || !email.trim()) {
        throw new Error(ro.recenzii.errors.required);
      }

      // reCAPTCHA
      const token = recaptchaRef.current?.getValue();
      if (!token) throw new Error(ro.recenzii.errors.captcha);
      let photoURL = "/images/imgaine1.svg";
      const inputEmail = normalizeEmail(email);
      const currentEmail = normalizeEmail(currentUser?.email);
      if (currentUser && currentEmail && currentEmail === inputEmail) {
        photoURL = currentUser.photoURL || "/images/imgaine1.svg";
      }

      // Salvam recenzia in Firestore
      await addDoc(recenziiRef, {
        nume: nume.trim(),
        email: email.trim(),
        text: text.trim(),
        rating: Number(rating),
        createdAt: serverTimestamp(),
        photoURL: photoURL,
      });

      // Resetam formularul si reCAPTCHA
      setNouaRecenzie({ nume: "", email: "", text: "", rating: 5 });
      recaptchaRef.current.reset();

      // Reincarcam lista de recenzii
      const snapshot = await getDocs(
        query(recenziiRef, orderBy("createdAt", "desc"))
      );
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRecenzii(data);
    } catch (err) {
      console.error("❌ Eroare la trimiterea recenziei:", err);
      setFormError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Componenta pentru stele
  const StarInput = ({ value, onChange }) => (
    <div className="star-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`star-btn ${n <= value ? "is-active" : ""}`}
          onClick={() => onChange(n)}
          aria-label={`${n} ${ro.recenzii.starsLabel}`}
        >
          {n <= value ? "★" : "☆"}
        </button>
      ))}
    </div>
  );

  return (
    <section id="recenzii" className="recenzii-section">
      <div className="recenzii-container">
        {/* Formular */}
        <div className="add-review">
          <h2 className="title">{ro.recenzii.title}</h2>
          <form onSubmit={handleSubmit} className="form">
            <input
              type="text"
              name="nume"
              placeholder={ro.recenzii.placeholders.nume}
              value={nouaRecenzie.nume}
              onChange={handleChange}
              className="input"
            />
            <input
              type="email"
              name="email"
              placeholder={ro.recenzii.placeholders.email}
              value={nouaRecenzie.email}
              onChange={handleChange}
              className="input"
            />

            <div className="field-inline">
              <label className="label">{ro.recenzii.labelRating}</label>
              <StarInput
                value={nouaRecenzie.rating}
                onChange={(val) =>
                  setNouaRecenzie((p) => ({ ...p, rating: val }))
                }
              />
            </div>

            <textarea
              name="text"
              rows="4"
              placeholder={ro.recenzii.placeholders.text}
              value={nouaRecenzie.text}
              onChange={handleChange}
              className="textarea"
            />

            {/* 🔹 reCAPTCHA */}
            <div className="bot-check-row flex justify-center py-2">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                size="normal"
              />
            </div>

            <button
              type="submit"
              className="btn-orange mt-3"
              disabled={loading}
            >
              {loading ? ro.recenzii.buttonLoading : ro.recenzii.button}
            </button>

            {formError && (
              <p className="text-red-600 text-sm mt-2">{formError}</p>
            )}
          </form>
        </div>

        {/* Lista de recenzii */}
        <div className="reviews-gallery">
          <div className="reviews-scroll">
            {recenzii.length > 0 ? (
              recenzii.map((r) => (
                <div key={r.id} className="review-card">
                  <div className="review-avatar-icon">
                    <img
                      src={r.photoURL || "/images/imgaine1.svg"}
                      alt="avatar"
                      className="w-16 h-16 rounded-full object-cover border-2 border-orange-400"
                    />
                  </div>

                  <div className="review-stars">
                    {"★".repeat(r.rating)}
                    {"☆".repeat(5 - r.rating)}
                  </div>
                  <h3 className="review-name">{r.nume}</h3>
                  <p className="text-xs text-gray-500">{r.email}</p>
                  <p className="review-text">{r.text}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center">
                {ro.recenzii.empty}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Recenzii;
